/* eslint no-console: 0 */
import { KeycloakService } from 'keycloak-angular';
import { EnvService } from '../../configuration/env.service';
import { FopLoginMode, IFopConfig, IFopTokens } from '../../fop-mode/fop-mode.model';
import { FopModeService } from '../../fop-mode/fop-mode.service';
import { LogService } from '../../logging/log.service';
import { flatMap, of } from 'rxjs';
import { WebSocketService } from '../../server-communication/services/websocket.service';
import { WebSocketMessageType } from '../../../../model/web-socket-message';
import { StaticRole } from '../../../../model/roles';
import jwtDecode from 'jwt-decode';

export function keycloakInitializer(
    keycloak: KeycloakService,
    env: EnvService,
    fopModeService: FopModeService,
    logService: LogService,
    webSocketService: WebSocketService
): () => Promise<boolean> {
    const LOG_SOURCE = 'KeycloakInitializer';
    return (): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            const initOptions: Keycloak.KeycloakInitOptions = { checkLoginIframe: false, onLoad: 'login-required' };
            fopModeService
                .getFopConfig()
                .pipe(
                    flatMap((fopConfig: IFopConfig) =>
                        fopConfig.loginMode === FopLoginMode.FopLogin
                            ? fopModeService.getFopTokens(fopConfig)
                            : of(undefined)
                    )
                )
                .subscribe((fopTokens: IFopTokens) => {
                    if (!fopTokens) {
                        keycloak
                            .init({
                                config: env.config.keycloak,
                                loadUserProfileAtStartUp: true,
                                initOptions: initOptions
                            })
                            .then(() => {
                                validateUserAccess(keycloak, webSocketService)
                                    .then(_ => {
                                        logService.info(LOG_SOURCE, 'User Login Initialization successful', {});
                                        resolve(true);
                                    })
                                    .catch(error => {
                                        logService.info(LOG_SOURCE, ' User LoginInitialization failed', error);
                                        reject(error);
                                    });
                            })
                            .catch(error => {
                                logService.info(LOG_SOURCE, ' User LoginInitialization failed', error);
                                reject(error);
                            });
                    } else {
                        initOptions.token = fopTokens.accessToken;
                        initOptions.refreshToken = fopTokens.refreshToken;
                        initOptions.idToken = fopTokens.idToken;
                        keycloak
                            .init({
                                config: env.config.keycloak,
                                loadUserProfileAtStartUp: true,
                                initOptions: initOptions
                            })
                            .then(() => {
                                logService.info(LOG_SOURCE, 'FOP Login Initialization successful', {});
                                resolve(true);
                            })
                            .catch(error => {
                                logService.info(LOG_SOURCE, 'FOP LoginInitialization failed', error);
                                reject(error);
                            });
                    }
                });
        });
    };
}

function validateUserAccess(keycloak: KeycloakService, webSocketService: WebSocketService) {
    return new Promise((resolve, reject) => {
        return keycloak
            .getToken()
            .then(token => {
                const decodedToken = jwtDecode(token) as any;
                if (decodedToken?.resource_access?.mop?.roles?.includes(StaticRole.LOGIN)) {
                    resolve(true);
                } else {
                    reportSiemEvent(webSocketService);
                    logoutOnNetworkTimeout(keycloak, reject);
                }
            })
            .catch(error => {
                reject(error);
            });
    });
}

// Trigger timeout if server does not acknowledge SIEM event within 10 seconds
function logoutOnNetworkTimeout(keycloak: KeycloakService, reject) {
    setTimeout(() => {
        keycloak.logout().finally(() => {
            //Unreachable code, will trigger browser refresh before this is executed
            reject(`User does not have the ${StaticRole.LOGIN} role`);
        });
    }, 10000);
}

function reportSiemEvent(webSocketService: WebSocketService) {
    //SIEM EVENT will trigger token validation which checks the roles and reports siem event
    return webSocketService.sendMessage(WebSocketMessageType.SiemEvent, '');
}
